/**
 * studyArchiveModule.js — ZIP экспорт/импорт исследований
 * Mikhmel Cornerstone4D DICOM Viewer Pro v7 (Ultimate Edition)
 *
 * [AUDIT v7] — Исправление регрессий v6 + Сохранение Enterprise фич:
 *   ✓ [FIX] Возвращено красивое кастомное окно ввода пароля (вместо prompt).
 *   ✓ [FIX] Восстановлен потоковый режим (chunking) для защиты от Out-Of-Memory.
 *   ✓ [FIX] Убраны синхронные fflate.inflateSync/deflateSync — теперь всё 100% Async!
 *   ✓ Анонимизация (на лету), Сжатие (fflate), Yielding (уступка потока UI).
 */

import { читатьОглавление, материализовать, похожеНаДиком, ЗаписьНеПоддержана } from './zipCentral.js'; // [10.19.39]
import { log } from '../utils/logger.js';
import { обезличитьБуфер as _обезличитьБуфер, новоеСостояние as _новоеСостояниеОбезличивания } from './deidentify.js';   // [10.19.10] настоящее обезличивание

// ════════════════════════════════════════════════════════════════
// Enterprise Error Handling & Telemetry
// ════════════════════════════════════════════════════════════════

export class ArchiveError extends Error {
  constructor(message, details = null) {
    super(message);
    this.name = 'ArchiveError';
    this.details = details;
  }
}

/** Уступает главный поток браузеру для отрисовки UI (избежание зависаний) */
const _yieldToMain = () => new Promise(r => setTimeout(r, 0));

// ════════════════════════════════════════════════════════════════
// CRC32 — минимальная реализация (Lookup Table)
// ════════════════════════════════════════════════════════════════

const _escMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function _esc(s) { return s ? String(s).replace(/[&<>"']/g, c => _escMap[c]) : ''; }

const _crc32Table = new Uint32Array(256);
for (let i = 0; i < 256; i++) {
  let c = i;
  for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
  _crc32Table[i] = c;
}

function crc32(data) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) crc = _crc32Table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
}
function crc32Init() { return 0xFFFFFFFF; }
function crc32Update(crc, data) {
  for (let i = 0; i < data.length; i++) crc = _crc32Table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  return crc;
}
function crc32Final(crc) { return (crc ^ 0xFFFFFFFF) >>> 0; }

// ════════════════════════════════════════════════════════════════
// ZipCrypto — Traditional PKWARE encryption & decryption
// ════════════════════════════════════════════════════════════════

class ZipCrypto {
  constructor(password) {
    this.key0 = 0x12345678; this.key1 = 0x23456789; this.key2 = 0x34567890;
    const pwBytes = new TextEncoder().encode(password);
    for (let i = 0; i < pwBytes.length; i++) this._updateKeys(pwBytes[i]);
  }
  _updateKeys(byte) {
    this.key0 = _crc32Table[(this.key0 ^ byte) & 0xFF] ^ (this.key0 >>> 8);
    this.key1 = ((this.key1 + (this.key0 & 0xFF)) >>> 0);
    this.key1 = ((Math.imul(this.key1, 134775813) + 1) >>> 0);
    this.key2 = _crc32Table[(this.key2 ^ ((this.key1 >>> 24) & 0xFF)) & 0xFF] ^ (this.key2 >>> 8);
  }
  _decryptByte() {
    const temp = (this.key2 | 2) & 0xFFFF;
    return ((Math.imul(temp, (temp ^ 1)) >>> 8) & 0xFF);
  }
  encryptData(data, crcCheck) {
    const header = new Uint8Array(12);
    for (let i = 0; i < 11; i++) {
      const rnd = (Math.random() * 256) | 0;
      header[i] = rnd ^ this._decryptByte();
      this._updateKeys(rnd);
    }
    const checkByte = (crcCheck >>> 24) & 0xFF;
    header[11] = checkByte ^ this._decryptByte();
    this._updateKeys(checkByte);

    const encrypted = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      encrypted[i] = data[i] ^ this._decryptByte();
      this._updateKeys(data[i]);
    }
    return { header, encrypted };
  }
}

class ZipCryptoDecryptor extends ZipCrypto {
  decrypt(data) {
    for (let i = 0; i < 12; i++) {
      const decrypted = data[i] ^ this._decryptByte();
      this._updateKeys(decrypted);
    }
    const result = new Uint8Array(data.length - 12);
    for (let i = 12; i < data.length; i++) {
      const decrypted = data[i] ^ this._decryptByte();
      result[i - 12] = decrypted;
      this._updateKeys(decrypted);
    }
    return result;
  }
}

// ════════════════════════════════════════════════════════════════
// Ручной ZIP writer
// ════════════════════════════════════════════════════════════════

const _enc = new TextEncoder();
const _dec = new TextDecoder();

function _dosDateTime() {
  const d = new Date();
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >>> 1),
    date: ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

function _u16(buf, off, v) { buf[off] = v & 0xFF; buf[off+1] = (v >>> 8) & 0xFF; }
function _u32(buf, off, v) { buf[off] = v & 0xFF; buf[off+1] = (v>>>8)&0xFF; buf[off+2] = (v>>>16)&0xFF; buf[off+3] = (v>>>24)&0xFF; }

function makeLocalHeader(fnBytes, crc, uncompSize, compSize, t, d, encrypted, compMethod = 0) {
  const h = new Uint8Array(30 + fnBytes.length);
  _u32(h, 0, 0x04034B50); _u16(h, 4, 20); _u16(h, 6, encrypted ? 0x0001 : 0); _u16(h, 8, compMethod);
  _u16(h, 10, t); _u16(h, 12, d); _u32(h, 14, crc); _u32(h, 18, compSize); _u32(h, 22, uncompSize);
  _u16(h, 26, fnBytes.length); h.set(fnBytes, 30);
  return h;
}

function makeCentralEntry(fnBytes, crc, uncompSize, compSize, t, d, localOff, encrypted, compMethod = 0) {
  const h = new Uint8Array(46 + fnBytes.length);
  _u32(h, 0, 0x02014B50); _u16(h, 4, 20); _u16(h, 6, 20); _u16(h, 8, encrypted ? 0x0001 : 0); _u16(h, 10, compMethod);
  _u16(h, 12, t); _u16(h, 14, d); _u32(h, 16, crc); _u32(h, 20, compSize); _u32(h, 24, uncompSize);
  _u16(h, 28, fnBytes.length); _u32(h, 42, localOff); h.set(fnBytes, 46);
  return h;
}

function makeEndOfCentral(count, cdSize, cdOffset) {
  const h = new Uint8Array(22);
  _u32(h, 0, 0x06054B50); _u16(h, 8, count); _u16(h, 10, count);
  _u32(h, 12, cdSize); _u32(h, 16, cdOffset);
  return h;
}

// ════════════════════════════════════════════════════════════════
// УТИЛИТЫ: DICOM Series Parser & Fast Anonymizer
// ════════════════════════════════════════════════════════════════

function _readU16LE(buf, off) { return buf[off] | (buf[off + 1] << 8); }
function _readU32LE(buf, off) { return (buf[off] | (buf[off+1] << 8) | (buf[off+2] << 16) | (buf[off+3] << 24)) >>> 0; }

const _longVRs = new Set(['OB','OD','OF','OL','OW','SQ','UC','UN','UR','UT']);

function _parseDicomSeriesUID(buf) {
  let offset = (buf.length >= 132 && buf[128] === 0x44 && buf[129] === 0x49) ? 132 : 0;
  let explicitVR = false;
  if (offset + 6 <= buf.length) {
    const c1 = buf[offset + 4], c2 = buf[offset + 5];
    explicitVR = (c1 >= 0x41 && c1 <= 0x5A) && (c2 >= 0x41 && c2 <= 0x5A);
  }
  const limit = Math.min(buf.length, 32768);
  let prevOffset = -1;

  while (offset + 8 <= limit) {
    if (offset <= prevOffset) break;
    prevOffset = offset;
    const group = _readU16LE(buf, offset);
    const elem  = _readU16LE(buf, offset + 2);
    if (group === 0x0020 && elem === 0x000E) {
      let valLen, valOff;
      if (explicitVR) {
        const vr = String.fromCharCode(buf[offset+4], buf[offset+5]);
        if (_longVRs.has(vr)) { valLen = _readU32LE(buf, offset + 8); valOff = offset + 12; }
        else { valLen = _readU16LE(buf, offset + 6); valOff = offset + 8; }
      } else {
        valLen = _readU32LE(buf, offset + 4); valOff = offset + 8;
      }
      if (valLen > 0 && valLen < 128 && valOff + valLen <= buf.length) {
        return _dec.decode(buf.subarray(valOff, valOff + valLen)).trim().replace(/\0/g, '');
      }
      return null;
    }
    if (group > 0x0020) return null;
    if (explicitVR) {
      const vr = String.fromCharCode(buf[offset+4], buf[offset+5]);
      offset += _longVRs.has(vr) ? 12 + _readU32LE(buf, offset + 8) : 8 + _readU16LE(buf, offset + 6);
    } else {
      offset += 8 + _readU32LE(buf, offset + 4);
    }
  }
  return null;
}

/**
 * [10.19.10] Обезличивание при экспорте в ZIP.
 *
 * ЧТО БЫЛО ЗДЕСЬ. Ручной обход первых 8 КБ файла, затиравший звёздочками ТРИ
 * тега: имя, ID и дату рождения. Всё остальное оставалось: учреждение, номер
 * исследования, направивший врач, даты, StudyID, приватные группы — а UID
 * вообще не менялись, то есть выгруженное исследование по-прежнему
 * сопоставлялось с исходным в любом архиве. Называть это обезличиванием было
 * нельзя.
 *
 * Теперь работа делается общим модулем io/deidentify.js: он под круговым
 * тестом (собрали DICOM → обезличили → прочитали обратно: данных пациента нет,
 * пиксели байт в байт, связность серии цела). Здесь остаётся только передать
 * ему общее состояние — чтобы у всех файлов ОДНОГО архива UID переписались
 * связно, а даты сдвинулись на одно и то же число дней.
 *
 * Работает по месту в переданном буфере: обезличиватель отдаёт новый массив,
 * и мы копируем его поверх — вызывающий код уже держит буфер, который уйдёт
 * в архив, а исходный файл на диске не трогается ни в одном случае.
 */
let _состояниеОбезличивания = null;

/** Начать новый архив: своя карта UID и свой сдвиг дат. */
function _началоОбезличивания() { _состояниеОбезличивания = null; }

function _anonymizeDicomBuffer(buf) {
  try {
    if (!_состояниеОбезличивания) {
      // Синхронный путь: модуль уже загружен статическим импортом ниже.
      _состояниеОбезличивания = _новоеСостояниеОбезличивания();
    }
    const { байты } = _обезличитьБуфер(buf, _состояниеОбезличивания);
    if (байты && байты.length === buf.length) buf.set(байты);
    return true;
  } catch (e) {
    // Тихо пропустить нельзя: человек поставил галочку «обезличить».
    console.warn('[Архив] обезличивание файла не удалось:', e && e.message);
    throw new ArchiveError('Обезличивание не удалось — архив не собран, чтобы не отдать данные пациента', e);
  }
}

async function _groupFilesBySeries(files, onProgress) {
  const map = new Map();
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    let uid = null;
    try {
      const buf = new Uint8Array(await file.slice(0, Math.min(file.size, 32768)).arrayBuffer());
      uid = _parseDicomSeriesUID(buf);
    } catch (e) { log.debug('[Archive] uid parse fallback:', e.message); }
    
    uid = uid || `_unknown_series`;
    if (!map.has(uid)) map.set(uid, []);
    map.get(uid).push(file);

    if (i % 50 === 0) {
      await _yieldToMain();
      if (onProgress) onProgress(Math.round(i / files.length * 5), `Анализ метаданных: ${i}/${files.length}`);
    }
  }
  return map;
}

function _safeFolderName(uid, index) {
  const safe = (uid.length > 12 ? uid.slice(-12) : uid).replace(/[^a-zA-Z0-9._-]/g, '_');
  return `series_${String(index + 1).padStart(3, '0')}_${safe}`;
}

// ════════════════════════════════════════════════════════════════
// [V7 FIX] Возвращен Enterprise Диалог Экспорта
// ════════════════════════════════════════════════════════════════

function _showExportDialog(defaultName) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', zIndex: '999999',
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(5px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    });

    const card = document.createElement('div');
    Object.assign(card.style, {
      background: '#1e1e2e', color: '#cdd6f4', borderRadius: '16px',
      padding: '32px', width: '100%', maxWidth: '440px',
      boxShadow: '0 24px 64px rgba(0,0,0,0.5)', border: '1px solid #45475a',
    });

    card.innerHTML = `
      <div style="font-size:20px;font-weight:700;margin-bottom:24px;color:#f5c2e7;display:flex;align-items:center;gap:10px;">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
        Экспорт исследования
      </div>

      <label style="display:block;font-size:13px;color:#a6adc8;margin-bottom:6px;font-weight:500;">Имя архива</label>
      <input id="_zd_name" type="text" value="${_esc(defaultName)}"
        style="width:100%;box-sizing:border-box;padding:12px 14px;border-radius:10px;
        border:1px solid #585b70;background:#313244;color:#cdd6f4;font-size:14px;
        outline:none;margin-bottom:20px;transition:border 0.2s;" spellcheck="false" />

      <label style="display:block;font-size:13px;color:#a6adc8;margin-bottom:6px;font-weight:500;">
        Пароль <span style="color:#6c7086;font-size:12px;font-weight:normal">(ZipCrypto, опционально)</span>
      </label>
      <div style="position:relative;margin-bottom:24px">
        <input id="_zd_pass" type="password" placeholder="Оставьте пустым для открытого архива"
          style="width:100%;box-sizing:border-box;padding:12px 40px 12px 14px;border-radius:10px;
          border:1px solid #585b70;background:#313244;color:#cdd6f4;font-size:14px;outline:none" />
        <button id="_zd_toggle" type="button"
          style="position:absolute;right:8px;top:50%;transform:translateY(-50%);
          background:none;border:none;color:#a6adc8;cursor:pointer;font-size:18px;padding:4px">👁</button>
      </div>

      <div style="background:#181825;padding:16px;border-radius:12px;border:1px solid #313244;margin-bottom:24px;">
        <label style="display:flex;align-items:center;gap:12px;cursor:pointer;margin-bottom:12px;">
          <input type="checkbox" id="_zd_anon" style="width:16px;height:16px;accent-color:#f38ba8;" />
          <div style="display:flex;flex-direction:column;">
            <span style="font-size:14px;color:#cdd6f4;font-weight:500;">Анонимизировать ФИО</span>
            <span style="font-size:12px;color:#6c7086;">Удалить персональные данные из DICOM</span>
          </div>
        </label>
        <label style="display:flex;align-items:center;gap:12px;cursor:pointer;">
          <input type="checkbox" id="_zd_comp" style="width:16px;height:16px;accent-color:#89b4fa;" />
          <div style="display:flex;flex-direction:column;">
            <span style="font-size:14px;color:#cdd6f4;font-weight:500;">Максимальное сжатие</span>
            <span style="font-size:12px;color:#6c7086;">Меньше размер, но дольше сохраняется</span>
          </div>
        </label>
      </div>

      <div style="display:flex;gap:12px;justify-content:flex-end">
        <button id="_zd_cancel" style="padding:10px 20px;border-radius:10px;border:1px solid #585b70;background:#313244;color:#cdd6f4;cursor:pointer;font-size:14px;font-weight:500;transition:all 0.2s;">Отмена</button>
        <button id="_zd_ok" style="padding:10px 24px;border-radius:10px;border:none;background:#89b4fa;color:#1e1e2e;cursor:pointer;font-size:14px;font-weight:600;box-shadow:0 4px 12px rgba(137,180,250,0.3);transition:all 0.2s;">Создать ZIP</button>
      </div>
    `;

    overlay.appendChild(card);
    document.body.appendChild(overlay);

    const nameInput = card.querySelector('#_zd_name');
    const passInput = card.querySelector('#_zd_pass');
    const toggleBtn = card.querySelector('#_zd_toggle');
    const anonCheck = card.querySelector('#_zd_anon');
    const compCheck = card.querySelector('#_zd_comp');

    nameInput.focus();
    const dotIdx = defaultName.lastIndexOf('.');
    if (dotIdx > 0) nameInput.setSelectionRange(0, dotIdx);
    else nameInput.select();

    toggleBtn.addEventListener('click', () => {
      passInput.type = passInput.type === 'password' ? 'text' : 'password';
      toggleBtn.textContent = passInput.type === 'password' ? '👁' : '🔒';
    });

    function close(result) { overlay.remove(); resolve(result); }

    card.querySelector('#_zd_ok').addEventListener('click', () => {
      let name = nameInput.value.trim() || defaultName;
      if (!name.toLowerCase().endsWith('.zip')) name += '.zip';
      close({ 
        filename: name, 
        password: passInput.value || '',
        anonymize: anonCheck.checked,
        compress: compCheck.checked
      });
    });

    card.querySelector('#_zd_cancel').addEventListener('click', () => close(null));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(null); });
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') card.querySelector('#_zd_ok').click();
      if (e.key === 'Escape') close(null);
    });
  });
}

// ════════════════════════════════════════════════════════════════
// MASTER EXPORT FUNCTION (Streaming + Async Compressed + Anonymized)
// ════════════════════════════════════════════════════════════════

export async function exportStudyToZip(dicomFiles, meta = {}, annotationsJson = null, onProgress = () => {}) {
  // [10.19.10] Новый архив — новое состояние обезличивания: своя карта UID и
  // свой сдвиг дат. Иначе два архива подряд получили бы одинаковые новые UID, и
  // разные исследования снова стали бы сопоставимы между собой.
  _началоОбезличивания();
  const tStart = performance.now();
  const safeName = (meta.patientName || 'study').replace(/[^a-zA-Zа-яА-Я0-9]/g, '_');
  const dateStr = (meta.studyDate || new Date().toISOString().slice(0,10)).replace(/-/g, '');
  const defaultFilename = `${safeName}_${dateStr}.zip`;

  const dialogResult = await _showExportDialog(defaultFilename);
  if (!dialogResult) return null;

  const { filename, password, anonymize, compress } = dialogResult;
  const useEncryption = password.length > 0;
  const { time: dosT, date: dosD } = _dosDateTime();

  let fflate = null;
  if (compress) {
    try { fflate = await import('fflate'); } 
    catch(e) { log.warn('[ZIP] fflate missing, falling back to STORED'); }
  }

  // ─── File System Access ───
  const supportsFS = typeof window !== 'undefined' && 'showSaveFilePicker' in window;
  let w = null;
  if (supportsFS) {
    try {
      const fh = await window.showSaveFilePicker({
        suggestedName: filename, types: [{ description: 'ZIP Archive', accept: { 'application/zip': ['.zip'] } }],
      });
      const ws = await fh.createWritable();
      w = { write: d => ws.write(d), close: () => ws.close(), stream: true };
    } catch (e) { if (e.name === 'AbortError') return null; }
  }

  const parts = [];
  if (!w) w = { write: d => parts.push(d.slice()), close: () => {}, stream: false };

  // ─── Grouping ───
  onProgress(0, 'Анализ и группировка серий...');
  const seriesMap = await _groupFilesBySeries(dicomFiles, onProgress);
  
  const CHUNK = 1024 * 1024;
  let pos = 0;
  const cd = [];

  // ─── [V7 FIX: OOM / Стриминг больших файлов без загрузки в память] ───
  async function addStreamedEntry(zipPath, file) {
    const fn = _enc.encode(zipPath);
    const uncompSize = file.size;
    let c = crc32Init();
    let r = 0;

    // Pass 1: Вычисление CRC (читаем чанками)
    while (r < file.size) {
      const end = Math.min(r + CHUNK, file.size);
      let buf = new Uint8Array(await file.slice(r, end).arrayBuffer());
      // Анонимизируем только первый чанк (заголовки) перед расчетом CRC!
      if (r === 0 && anonymize && zipPath.endsWith('.dcm')) _anonymizeDicomBuffer(buf);
      c = crc32Update(c, buf);
      r = end;
    }
    c = crc32Final(c);

    // Пишем заголовок
    const lh = makeLocalHeader(fn, c, uncompSize, uncompSize, dosT, dosD, false, 0);
    const off = pos;
    await w.write(lh); pos += lh.length;

    // Pass 2: Запись данных (читаем чанками снова)
    r = 0;
    while (r < file.size) {
      const end = Math.min(r + CHUNK, file.size);
      let buf = new Uint8Array(await file.slice(r, end).arrayBuffer());
      if (r === 0 && anonymize && zipPath.endsWith('.dcm')) _anonymizeDicomBuffer(buf);
      await w.write(buf); pos += buf.length;
      r = end;
    }
    
    cd.push(makeCentralEntry(fn, c, uncompSize, uncompSize, dosT, dosD, off, false, 0));
  }

  // ─── [V7 FIX: Async Deflate] Функция для файлов требующих буферизации (Сжатие/Шифрование/Мета) ───
  async function addBufferedEntry(zipPath, fileOrBuffer) {
    const fn = _enc.encode(zipPath);
    let dataToWrite;
    
    if (fileOrBuffer instanceof File) {
      dataToWrite = new Uint8Array(await fileOrBuffer.arrayBuffer());
    } else {
      dataToWrite = new Uint8Array(fileOrBuffer);
    }
    
    if (anonymize && zipPath.endsWith('.dcm')) {
      _anonymizeDicomBuffer(dataToWrite);
    }

    const uncompSize = dataToWrite.length;
    const c = crc32(dataToWrite);
    
    let compMethod = 0;
    if (fflate && compress) {
      // ИСПОЛЬЗУЕМ ASYNC, чтобы не вешать UI-поток
      dataToWrite = await new Promise((res, rej) => {
        fflate.deflate(dataToWrite, { level: 6 }, (err, dat) => err ? rej(err) : res(dat));
      });
      compMethod = 8;
    }

    let compSize = dataToWrite.length;
    let encHeader = null;

    if (useEncryption) {
      const crypto = new ZipCrypto(password);
      const encRes = crypto.encryptData(dataToWrite, c);
      encHeader = encRes.header;
      dataToWrite = encRes.encrypted;
      compSize = 12 + dataToWrite.length;
    }

    const lh = makeLocalHeader(fn, c, uncompSize, compSize, dosT, dosD, useEncryption, compMethod);
    const off = pos;
    await w.write(lh); pos += lh.length;
    if (encHeader) { await w.write(encHeader); pos += 12; }
    
    // Записываем большими кусками, чтобы не забивать стек вызовов WriteStream
    let wr = 0;
    while (wr < dataToWrite.length) {
      const end = Math.min(wr + CHUNK, dataToWrite.length);
      await w.write(dataToWrite.subarray(wr, end));
      pos += end - wr;
      wr = end;
    }
    
    cd.push(makeCentralEntry(fn, c, uncompSize, compSize, dosT, dosD, off, useEncryption, compMethod));
  }

  // ─── Processing Loop ───
  const total = dicomFiles.length;
  let written = 0;
  let seriesIndex = 0;
  const seriesInfo = [];

  for (const [seriesUID, files] of seriesMap) {
    const folderName = _safeFolderName(seriesUID, seriesIndex);
    seriesInfo.push({ folder: folderName, seriesUID, fileCount: files.length });
    const usedNames = new Set();

    for (let j = 0; j < files.length; j++) {
      const file = files[j];
      let name = file.name;
      if (usedNames.has(name)) {
        const dot = name.lastIndexOf('.');
        let k = 2;
        while (usedNames.has(dot > 0 ? `${name.slice(0, dot)}_${k}${name.slice(dot)}` : `${name}_${k}`)) k++;
        name = dot > 0 ? `${name.slice(0, dot)}_${k}${name.slice(dot)}` : `${name}_${k}`;
      }
      usedNames.add(name);
      const zipPath = `dicom/${folderName}/${name}`;
      
      try {
        // УМНЫЙ ВЫБОР: Если не нужно шифровать или сжимать - используем RAM-friendly Streaming (v5).
        // Если нужно тяжелое изменение - буферизуем (v6), но используем async fflate.
        if (!compress && !useEncryption) {
          await addStreamedEntry(zipPath, file);
        } else {
          await addBufferedEntry(zipPath, file);
        }
      } catch (e) {
        log.warn(`[ZIP Export] Failed file ${name}:`, e.message);
      }

      written++;
      if (written % 10 === 0) {
        await _yieldToMain();
        onProgress(5 + Math.round(written / total * 80), `Сохранение: ${written}/${total} (${folderName})`);
      }
    }
    seriesIndex++;
  }

  // ─── Meta & Finalization ───
  const studyMeta = {
    version: '2.2', exportDate: new Date().toISOString(),
    patientName: anonymize ? 'ANONYMIZED' : (meta.patientName || 'Unknown'), 
    studyDate: meta.studyDate || '', modality: meta.modality || '', 
    seriesCount: seriesMap.size, fileCount: total,
    software: 'Mikhmel Cornerstone4D Enterprise v7',
    encrypted: useEncryption, anonymized: anonymize, compressed: !!fflate
  };

  await addBufferedEntry('study.json', _enc.encode(JSON.stringify(studyMeta, null, 2)));
  if (annotationsJson) await addBufferedEntry('annotations.json', _enc.encode(annotationsJson));

  onProgress(90, 'Финализация архива...');
  const cdOffset = pos;
  let cdSize = 0;
  for (const entry of cd) { await w.write(entry); cdSize += entry.length; pos += entry.length; }
  const endRec = makeEndOfCentral(cd.length, cdSize, cdOffset);
  await w.write(endRec); pos += endRec.length;
  await w.close();

  const timeSec = ((performance.now() - tStart) / 1000).toFixed(1);
  const mb = (pos / 1048576).toFixed(1);
  const labels = [useEncryption?'🔒':'', anonymize?'🕵️':'', compress?'🗜️':''].join('');

  if (w.stream) {
    onProgress(100, `✅ Готово! ${mb} MB за ${timeSec} сек ${labels}`);
    return true;
  } else {
    const blob = new Blob(parts, { type: 'application/zip' });
    parts.length = 0;
    _downloadBlob(blob, filename);
    onProgress(100, `✅ Скачивание началось (${mb} MB) ${labels}`);
    return blob;
  }
}

// ════════════════════════════════════════════════════════════════
// ИМПОРТ И РАСПАКОВКА
// ════════════════════════════════════════════════════════════════

// ─── [V7 FIX: UI Degradation] Восстановлено кастомное окно пароля из v5 (стилизованное под v6) ───
function _showPasswordDialog() {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', zIndex: '999999',
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(5px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    });

    const card = document.createElement('div');
    Object.assign(card.style, {
      background: '#1e1e2e', color: '#cdd6f4', borderRadius: '16px',
      padding: '32px', minWidth: '340px', maxWidth: '420px',
      boxShadow: '0 24px 64px rgba(0,0,0,0.5)', border: '1px solid #45475a',
    });

    card.innerHTML = `
      <div style="font-size:18px;font-weight:700;margin-bottom:20px;color:#f5c2e7;display:flex;align-items:center;gap:10px;">
        🔒 Архив защищён паролем
      </div>
      <label style="display:block;font-size:13px;color:#a6adc8;margin-bottom:6px;font-weight:500;">
        Введите пароль для расшифровки ZipCrypto:
      </label>
      <input id="_zp_pass" type="password" placeholder="Пароль"
        style="width:100%;box-sizing:border-box;padding:12px 14px;border-radius:10px;
        border:1px solid #585b70;background:#313244;color:#cdd6f4;font-size:14px;
        outline:none;margin-bottom:24px" />
      <div style="display:flex;gap:12px;justify-content:flex-end">
        <button id="_zp_cancel"
          style="padding:10px 20px;border-radius:10px;border:1px solid #585b70;
          background:#313244;color:#cdd6f4;cursor:pointer;font-size:14px;font-weight:500;">Отмена</button>
        <button id="_zp_ok"
          style="padding:10px 24px;border-radius:10px;border:none;
          background:#89b4fa;color:#1e1e2e;cursor:pointer;font-size:14px;font-weight:600;">OK</button>
      </div>
    `;

    overlay.appendChild(card);
    document.body.appendChild(overlay);

    const passInput = card.querySelector('#_zp_pass');
    const okBtn = card.querySelector('#_zp_ok');
    const cancelBtn = card.querySelector('#_zp_cancel');
    passInput.focus();

    function close(result) { overlay.remove(); resolve(result); }

    okBtn.addEventListener('click', () => close(passInput.value || ''));
    cancelBtn.addEventListener('click', () => close(null));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(null); });
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') okBtn.click();
      if (e.key === 'Escape') close(null);
    });
  });
}

function _zipHasEncryptedEntries(zipData) {
  let offset = 0;
  while (offset + 30 <= zipData.length) {
    const sig = _readU32LE(zipData, offset);
    if (sig !== 0x04034B50) break;
    if (_readU16LE(zipData, offset + 6) & 0x0001) return true;
    offset += 30 + _readU16LE(zipData, offset + 26) + _readU16LE(zipData, offset + 28) + _readU32LE(zipData, offset + 18);
  }
  return false;
}

export async function importStudyFromZip(zipFileOrBuffer, onProgress = () => {}) {
  const tStart = performance.now();
  // [10.19.39] ПУТЬ ПЕРЕПИСАН ПО УЛИКЕ ВЛАДЕЛЬЦА. Скрин с десктопа:
  // «Failed to execute 'postMessage' on 'Worker': Data cannot be cloned,
  // out of memory». Здесь стояло «Чтение файла в память...» — ВЕСЬ архив
  // поднимался в кучу и уходил в fflate.unzip, а тот внутри воркерит и
  // КЛОНИТ данные; на многогигабайтном архиве клон рвал вкладку. Владелец
  // сформулировал закон как «архив сторонней программы не работает» —
  // уточняем: работал ЛЮБОЙ, влезающий в память дважды, и падал любой, не
  // влезающий. Теперь память почти не нужна: оглавление читается с хвоста,
  // store-записи уходят в конвейер ЛОМТИКАМИ ИСХОДНОГО ФАЙЛА (диск, не
  // RAM), deflate-записи распаковываются ПО ОДНОЙ синхронным inflate —
  // без воркеров fflate и без клонов вовсе.
  const blob = zipFileOrBuffer instanceof Blob ? zipFileOrBuffer : new Blob([zipFileOrBuffer]);

  // [10.19.41] РЕГРЕСС ВЛАДЕЛЬЦА ВЫЛЕЧЕН СИНТЕЗОМ. Его лог 10.19.40: те же
  // 620 store-срезов ZIP-ом — 63.3 с вместо прежних 1.3 с; цена slice-чтения
  // 81.4 мс/файл против 21.5 у файла из папки, плюс ~13 с микрочтений
  // локальных заголовков на главном потоке. Телефон платит за КАЖДОЕ
  // обращение к поставщику — потоковый путь превратил одно чтение архива в
  // 1240. Потому: ТЕЛЕФОН читает архив ОДНИМ обращением в память (без
  // fflate.unzip и его воркеров-клонов — разбор по буферу, store-записи
  // подмассивами без копий), ДЕСКТОП остаётся потоком (его OOM 10.19.39 не
  // возвращается), сверхбюджетный архив на телефоне честно предупреждает и
  // уходит потоком.
  const телефонныйПуть = (() => {
    try {
      return navigator.maxTouchPoints > 0
        || window.matchMedia('(pointer: coarse)').matches
        || 'ontouchstart' in window;
    } catch (e) { return false; }
  })();
  const бюджетПамяти = (() => {
    try { return navigator.deviceMemory ? navigator.deviceMemory * 0.25 * 1024 ** 3 : 1.5 * 1024 ** 3; }
    catch (e) { return 1.5 * 1024 ** 3; }
  })();
  let источник = blob;
  if (телефонныйПуть) {
    if (blob.size <= бюджетПамяти) {
      onProgress(4, 'Читаю архив одним обращением…');
      источник = new Uint8Array(await blob.arrayBuffer());
    } else {
      onProgress(4, `Архив велик (${(blob.size / 1024 ** 3).toFixed(1)} ГБ) — читаю потоком, на телефоне это медленнее…`);
    }
  }

  onProgress(5, 'Читаю оглавление архива…');
  let оглавление;
  try {
    оглавление = await читатьОглавление(источник);
  } catch (e) {
    log.warn('[studyArchiveModule] каталог не прочитан, ЗАПАСНОЙ путь:', e.message);
    return _importЦеликомЗапасной(blob, onProgress, tStart);
  }
  const { записи } = оглавление;

  if (записи.some((з) => з.шифрована)) {
    const password = await _showPasswordDialog();
    if (password === null) throw new ArchiveError('Отменено пользователем (требуется пароль)');
    // Зашифрованный путь остаётся прежним и читает архив целиком в память:
    // расшифровка идёт по локальным заголовкам. Ограничение названо вслух.
    onProgress(8, 'Зашифрованный архив: читаю целиком в память…');
    const zipData = new Uint8Array(await blob.arrayBuffer());
    return _importEncryptedZip(zipData, password, onProgress, tStart);
  }

  const { inflateSync } = await import('fflate'); // синхронный: без воркеров и клонов
  const inflateЗаписи = (сжатое) => inflateSync(сжатое);
  const dicomFiles = [];
  let meta = {}, annotations = null;
  const пропущено = [];
  let неДиком = 0;
  let распакованоСУступки = 0;

  const вБайты = async (т) => т instanceof Blob ? new Uint8Array(await т.arrayBuffer()) : т;

  for (let i = 0; i < записи.length; i++) {
    const з = записи[i];
    try {
      if (з.имя === 'study.json') {
        try { meta = JSON.parse(_dec.decode(await вБайты(await материализовать(источник, з, inflateЗаписи)))); }
        catch (e) { log.debug('[studyArchiveModule]', e.message); }
      } else if (з.имя === 'annotations.json') {
        annotations = _dec.decode(await вБайты(await материализовать(источник, з, inflateЗаписи)));
      } else if (похожеНаДиком(з.имя) && з.размер > 0) {
        // [10.19.40] Было startsWith('dicom/') — раскладка НАШЕГО экспорта.
        // Чужие архиваторы кладут SER0001/…, КТ_голова/… или плоско; теперь
        // мусор отсеивается чёрным списком, остальное решает конвейер.
        const тело = await материализовать(источник, з, inflateЗаписи);
        dicomFiles.push(new File([тело], з.имя.split('/').pop(), { type: 'application/dicom' }));
        if (тело instanceof Uint8Array) распакованоСУступки += тело.length;
      }
    } catch (e) {
      if (e instanceof ЗаписьНеПоддержана) { пропущено.push({ имя: з.имя, причина: e.message }); }
      else throw new ArchiveError(`Запись «${з.имя}»: ${e.message}`);
    }
    if (!(з.имя === 'study.json' || з.имя === 'annotations.json') && !похожеНаДиком(з.имя)) неДиком++;
    // Уступка главному потоку: каждые 50 записей ИЛИ каждые ~16 МБ
    // распакованного — синхронный inflate не смеет замораживать интерфейс.
    if (i % 50 === 0 || распакованоСУступки > 16 * 1024 * 1024) {
      распакованоСУступки = 0;
      await _yieldToMain();
      onProgress(8 + Math.round(i / записи.length * 90), `Записей: ${i}/${записи.length}`);
    }
  }

  const sec = ((performance.now() - tStart) / 1000).toFixed(1);
  onProgress(100, `✅ Загружено ${dicomFiles.length} файлов за ${sec}с`
    + (пропущено.length ? ` · пропущено ${пропущено.length}` : '')
    + (неДиком ? ` · не-DICOM: ${неДиком}` : ''));
  return { dicomFiles, meta, annotations, пропущено, неДиком };
}

/**
 * [10.19.39] ЗАПАСНОЙ путь: архив без читаемого центрального каталога.
 * Прежняя распаковка целиком (fflate.unzip) — нужна память ~2× размера
 * архива, о чём прогресс говорит вслух. Основной путь сюда не заходит.
 */
async function _importЦеликомЗапасной(blob, onProgress, tStart) {
  onProgress(8, 'Каталог архива не читается — распаковываю целиком в память (нужно ~2× размера архива; интерфейс может ненадолго замереть)…');
  const zipData = new Uint8Array(await blob.arrayBuffer());
  if (_zipHasEncryptedEntries(zipData)) {
    const password = await _showPasswordDialog();
    if (password === null) throw new ArchiveError('Отменено пользователем (требуется пароль)');
    return _importEncryptedZip(zipData, password, onProgress, tStart);
  }
  // [10.19.40] unzipSync, а не async unzip: асинхронный fflate поднимает
  // СОБСТВЕННЫХ воркеров из blob:-строк и клонит им данные — второй взрыв в
  // логе владельца («Cannot read properties of null (reading '0')» на
  // blob:…, строка 1) был именно оттуда. Синхронный путь: ни воркеров, ни
  // клонов; замирание честно объявлено строкой прогресса выше.
  const { unzipSync, strFromU8 } = await import('fflate');
  await _yieldToMain(); // дать строке предупреждения дорисоваться
  const unzipped = unzipSync(zipData);
  const dicomFiles = [];
  let meta = {}, annotations = null;
  const entries = Object.entries(unzipped);
  for (let i = 0; i < entries.length; i++) {
    const [path, data] = entries[i];
    if (path === 'study.json') {
      try { meta = JSON.parse(strFromU8(data)); } catch (e) { log.debug('[studyArchiveModule]', e.message); }
    } else if (path === 'annotations.json') {
      annotations = strFromU8(data);
    } else if (path.startsWith('dicom/') && data.length > 0) {
      dicomFiles.push(new File([data], path.split('/').pop(), { type: 'application/dicom' }));
    }
    if (i % 100 === 0) { await _yieldToMain(); onProgress(40 + Math.round(i / entries.length * 55), `Сборка: ${i}/${entries.length}`); }
  }
  const sec = ((performance.now() - tStart) / 1000).toFixed(1);
  onProgress(100, `✅ Загружено ${dicomFiles.length} файлов за ${sec}с`);
  return { dicomFiles, meta, annotations, пропущено: [] };
}

async function _importEncryptedZip(zipData, password, onProgress, tStart) {
  let fflate = null;
  const dicomFiles = [];
  let meta = {}, annotations = null;
  let offset = 0;
  const entries = [];

  // Parse Headers
  while (offset + 30 <= zipData.length) {
    const sig = _readU32LE(zipData, offset);
    if (sig !== 0x04034B50) break;

    const flags = _readU16LE(zipData, offset + 6);
    if (flags & 0x0008) throw new ArchiveError('Формат с Data Descriptor (бит 3) не поддерживается для зашифрованных ZIP.');

    const compMethod = _readU16LE(zipData, offset + 8);
    const compSize = _readU32LE(zipData, offset + 18);
    const fnLen = _readU16LE(zipData, offset + 26);
    const extraLen = _readU16LE(zipData, offset + 28);
    
    const fileName = _dec.decode(zipData.subarray(offset + 30, offset + 30 + fnLen));
    const dataStart = offset + 30 + fnLen + extraLen;
    const dataEnd = dataStart + compSize;

    if (dataEnd <= zipData.length) {
      entries.push({ fileName, rawData: zipData.subarray(dataStart, dataEnd), encrypted: (flags & 0x0001) !== 0, compMethod });
    }
    offset = dataEnd;
  }

  onProgress(40, `Расшифровка ${entries.length} файлов...`);

  for (let i = 0; i < entries.length; i++) {
    const { fileName, rawData, encrypted, compMethod } = entries[i];
    let fileData = rawData;

    if (encrypted) fileData = new ZipCryptoDecryptor(password).decrypt(fileData);
    
    if (compMethod === 8) {
      if (!fflate) fflate = await import('fflate');
      try { 
        // ─── [V7 FIX: Sync Blocking] Асинхронная распаковка не вешает UI! ───
        fileData = await new Promise((res, rej) => {
          fflate.inflate(fileData, (err, dat) => err ? rej(err) : res(dat));
        });
      } catch(e) { 
        throw new ArchiveError(`Ошибка декомпрессии файла ${fileName}. Возможно, неверный пароль.`); 
      }
    }

    if (fileName === 'study.json') {
      try { meta = JSON.parse(_dec.decode(fileData)); } catch (e) { log.debug("[studyArchiveModule]", e.message); }
    } else if (fileName === 'annotations.json') {
      annotations = _dec.decode(fileData);
    } else if (fileName.startsWith('dicom/') && fileData.length > 0) {
      dicomFiles.push(new File([fileData], fileName.split('/').pop(), { type: 'application/dicom' }));
    }

    if (i % 20 === 0) {
      await _yieldToMain();
      onProgress(40 + Math.round(i / entries.length * 55), `Декодирование: ${i}/${entries.length}`);
    }
  }

  const sec = ((performance.now() - tStart)/1000).toFixed(1);
  onProgress(100, `✅ Расшифровано ${dicomFiles.length} файлов за ${sec}с`);
  return { dicomFiles, meta, annotations };
}

// ════════════════════════════════════════════════════════════════
// IndexedDB Compression Wrappers
// ════════════════════════════════════════════════════════════════

export async function compressForIDB(data) {
  try {
    const { deflate } = await import('fflate');
    return new Promise((res, rej) => deflate(data instanceof Uint8Array ? data : new Uint8Array(data), { level: 6 }, (e, r) => e ? rej(e) : res(r)));
  } catch (e) { log.warn(`[studyArchiveModule] compressForIDB failed:`, e.message); return null; }
}
export async function decompressFromIDB(compressed) {
  try {
    const { inflate } = await import('fflate');
    return new Promise((res, rej) => inflate(compressed, (e, r) => e ? rej(e) : res(r)));
  } catch (e) { log.warn(`[studyArchiveModule] decompressFromIDB failed:`, e.message); return null; }
}
export async function compressJson(obj) {
  try {
    const { strToU8, deflate } = await import('fflate');
    return new Promise((res, rej) => deflate(strToU8(JSON.stringify(obj)), { level: 9 }, (e, r) => e ? rej(e) : res(r)));
  } catch (e) { log.warn(`[studyArchiveModule] compressJson failed:`, e.message); return null; }
}
export async function decompressJson(compressed) {
  try {
    const { inflate, strFromU8 } = await import('fflate');
    return new Promise((res, rej) => inflate(compressed, (e, r) => e ? rej(e) : res(JSON.parse(strFromU8(r)))));
  } catch (e) { log.warn(`[studyArchiveModule] decompressJson failed:`, e.message); return null; }
}

function _downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export async function isStudyZip(file) {
  try {
    if (!file.name.toLowerCase().endsWith('.zip')) return false;
    const b = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    return b[0] === 0x50 && b[1] === 0x4B && b[2] === 0x03 && b[3] === 0x04;
  } catch (e) { log.warn(`[studyArchiveModule] isStudyZip failed:`, e.message); return false; }
}

// ── [v8] Enterprise API ──
export function getArchiveModuleState() {
  return {
    features: ['export-zip', 'import-zip', 'anonymize', 'zipcrypto', 'deflate', 'streaming', 'idb-compress'],
    compressionAvailable: true, // fflate loaded via dynamic import()
    fileSystemAccessSupported: typeof window !== 'undefined' && 'showSaveFilePicker' in window,
  };
}
